import React, { useEffect, useRef, useState, useCallback } from 'react'
import YouTube from 'react-youtube'
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react'
import useVideoPlayer from '../../hooks/useVideoPlayer'
import { formatTime } from '../../utils/timeFormat'

/**
 * MultiPlayer - Unified video player component
 * Supports R2/MP4, YouTube, and Vimeo (native iframe)
 *
 * @param {Object} props
 * @param {string} [props.type='r2'] - Video type: 'r2', 'youtube', or 'vimeo'
 * @param {string} props.src - Video URL or R2 path
 * @param {string} [props.videoId] - Explicit video ID (for YouTube)
 * @param {string} [props.poster] - Thumbnail image URL
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onVideoPlay] - Callback when video starts playing
 * @param {Function} [props.onVideoEnded] - Callback when video ends
 * @param {boolean} [props.autoFullscreen] - Auto-enter fullscreen when playing
 */
export default function MultiPlayer({
  type = 'r2',
  src,
  videoId,
  poster,
  className = '',
  onVideoPlay,
  onVideoEnded,
  autoFullscreen = false,
}) {
  const containerRef = useRef(null)

  const [isFullscreen, setIsFullscreen] = useState(false)

  // Inactivity timer for auto-hiding controls
  const [controlsVisible, setControlsVisible] = useState(false)
  const [lastMouseMove, setLastMouseMove] = useState(0)

  const {
    playerRef,
    ytPlayerRef,
    ytId,
    playing,
    volume,
    muted,
    currentTime,
    duration,
    showControls,
    setShowControls,
    handlePlay,
    handlePause,
    handleSeek,
    handleVolume,
    handleMuteToggle,
    handleFullscreen,
    enterFullscreen,
    handleTimeUpdate,
    handleVideoEnded,
    onYouTubeReady,
    onYouTubeStateChange,
  } = useVideoPlayer({ type, src, videoId, onEnded: onVideoEnded })

  // Auto-enter fullscreen when video starts playing (if enabled)
  const hasStartedRef = useRef(false)
  useEffect(() => {
    if (playing && !hasStartedRef.current) {
      hasStartedRef.current = true
      onVideoPlay?.()

      // Auto-enter fullscreen if enabled
      if (autoFullscreen) {
        // Small delay to ensure video is ready
        setTimeout(() => {
          enterFullscreen()
        }, 100)
      }
    }
    // Reset when video ends
    if (!playing) {
      hasStartedRef.current = false
    }
  }, [playing, onVideoPlay, autoFullscreen, enterFullscreen])

  // Track fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Keyboard controls (only when in fullscreen)
  useEffect(() => {
    if (!isFullscreen) return

    const handleKeyDown = (e) => {
      // Space: toggle play/pause
      if (e.code === 'Space') {
        e.preventDefault()
        playing ? handlePause() : handlePlay()
        return
      }

      // Arrow keys: seek (only when playing)
      if (!playing) return

      const seekAmount = 5 // seconds
      if (e.code === 'ArrowLeft') {
        e.preventDefault()
        handleSeek((currentTime - seekAmount) / duration)
      } else if (e.code === 'ArrowRight') {
        e.preventDefault()
        handleSeek((currentTime + seekAmount) / duration)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, playing, currentTime, duration, handlePlay, handlePause, handleSeek])

  // Auto-hide controls after 2s of inactivity when playing
  useEffect(() => {
    if (!playing) {
      // Always show controls when paused
      setControlsVisible(true)
      return
    }

    // When playing, show controls initially
    setControlsVisible(true)

    // Hide after 2 seconds of inactivity
    const timer = setTimeout(() => {
      setControlsVisible(false)
    }, 2000)

    return () => clearTimeout(timer)
  }, [playing, lastMouseMove])

  // YouTube player options
  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
      controls: 1,
      rel: 0,
      modestbranding: 1,
      iv_load_policy: 3,
      fs: 1,
      playsinline: 1,
      enablejsapi: 1,
    },
  }

  // Get Vimeo embed URL
  const getVimeoUrl = (url) => {
    const m = url?.match(/vimeo\.com\/(\d+)/)
    return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=0&controls=0` : url
  }

  const vimeoUrl = type === 'vimeo' ? getVimeoUrl(src) : null

  // Handle progress bar click
  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    handleSeek(percent)
  }

  return (
    <div
      ref={containerRef}
      className={`relative rounded-2xl overflow-hidden bg-black/40 group cursor-pointer ${className}`}
      style={{ opacity: 1 }}
      onMouseMove={() => {
        setLastMouseMove(Date.now())
        setShowControls(true)
      }}
      onMouseLeave={() => {
        setShowControls(false)
        setControlsVisible(false)
      }}
    >
      {/* Video Container */}
      <div
        className="relative aspect-video [&:fullscreen]:w-screen [&:fullscreen]:h-screen [&:fullscreen]:aspect-auto [&:fullscreen_&]:flex [&:fullscreen_&]:items-center [&:fullscreen_&]:justify-center"
        onMouseMove={() => {
          setLastMouseMove(Date.now())
          setShowControls(true)
        }}
      >
        {/* Default gradient background when no poster */}
        {!playing && !poster && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
        )}

        {/* Thumbnail - shown when paused */}
        {!playing && poster && (
          <img
            src={poster}
            alt="Video thumbnail"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Video Element (R2/MP4) - Always in DOM so ref is populated when play() is called */}
        {type === 'r2' && (
          <video
            ref={playerRef}
            src={src}
            preload="none"
            className={`w-full h-full object-cover z-10 ${playing ? 'relative' : 'hidden'}`}
            onEnded={handleVideoEnded}
            onTimeUpdate={handleTimeUpdate}
            onClick={playing ? handlePause : handlePlay}
          />
        )}

        {/* YouTube Player - lazy loaded */}
        {type === 'youtube' && ytId && playing && (
          <div className="w-full h-full">
            <YouTube
              videoId={ytId}
              opts={opts}
              onReady={onYouTubeReady}
              onStateChange={onYouTubeStateChange}
              className="w-full h-full"
            />
          </div>
        )}

        {/* Vimeo Player - lazy loaded */}
        {type === 'vimeo' && vimeoUrl && playing && (
          <div className="w-full h-full">
            <iframe
              src={vimeoUrl}
              className={`w-full h-full ${showControls || controlsVisible ? 'pointer-events-none' : ''}`}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {/* Play/Pause Button - Always visible when not playing (R2/MP4 only) */}
        {type !== 'youtube' && !playing && (
          <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
            <button
              className="multi-player-play-button relative w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center
                bg-white/60 border border-white/80
                hover:bg-white/80 hover:animate-pulse-scale transition-all duration-200 pointer-events-auto
                shadow-lg"
              onClick={handlePlay}
            >
              <Play size={20} md:size={26} className="text-white ml-1" fill="white" />
            </button>
          </div>
        )}

        {/* Pause Button - Show when playing with controls */}
        {type !== 'youtube' && playing && controlsVisible && (
          <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
            <button
              className="multi-player-play-button relative w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center
                bg-white/60 border border-white/80
                hover:bg-white/80 transition-all duration-200 pointer-events-auto
                shadow-lg"
              onClick={handlePause}
            >
              <Pause size={20} md:size={26} className="text-white" fill="white" />
            </button>
          </div>
        )}

        {/* Progress Bar - NOT for YouTube (uses native controls) */}
        {type !== 'youtube' && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[11px] bg-color-primary cursor-pointer
              group/progress transition-opacity duration-300 pointer-events-auto z-30"
            style={{ opacity: controlsVisible ? 1 : 0 }}
            onClick={handleProgressClick}
          >
            <div
              className="multi-player-progress-fill h-full bg-white/80 group-hover/progress:bg-white transition-colors"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
        )}

        {/* Controls Bar - NOT for YouTube (uses native controls) */}
        {type !== 'youtube' && (
          <div
            className={`absolute bottom-[11px] left-0 right-0 flex items-center justify-between
              px-5 py-3 pointer-events-auto z-40
              bg-gradient-to-t from-black/60 via-black/40 to-transparent
              transition-opacity duration-300`}
            style={{ opacity: controlsVisible ? 1 : 0 }}
          >
          {/* Time Display */}
          <div className="text-white text-xs font-medium">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          {/* Volume + Fullscreen - Right Aligned */}
          <div className="flex items-center gap-3">
            {/* Volume */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleMuteToggle}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Mute toggle"
              >
                {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <div className="multi-player-volume-slider relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={muted ? 0 : volume}
                  onChange={(e) => handleVolume(e.target.value)}
                  className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3
                    [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-white/80 [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-webkit-slider-thumb]:transition-all"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Fullscreen */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleFullscreen()
              }}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Fullscreen"
            >
              <Maximize size={20} />
            </button>
          </div>
        </div>
        )}
    </div>
  </div>
  )
}
